import { Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	apiQueryKey,
	useApiReopenPolicyResult,
	type EntityViolationsOut,
	type PolicyOut,
	type PolicyViolationOut,
} from '@repo/api-codegen';
import { Icon } from '@repo/foundations';
import { useCallback } from 'react';
import { queryClient } from '../../../../api';
import PolicySection from '../../PolicySection';
import type { PolicyTab } from '../../types';
import { IgnoredResourceRow } from './IgnoredResourceRow';

export function IgnoredResourcesTable({
	ignoredResults,
	policy,
	onTabChange,
	readOnly = false,
}: {
	ignoredResults: EntityViolationsOut[];
	policy: PolicyOut;
	onTabChange: (tab: PolicyTab) => void;
	readOnly?: boolean;
}) {
	const { mutateAsync: reopenPolicyResult } = useApiReopenPolicyResult();
	const handleReopenIssue = useCallback(
		async (e, violations: PolicyViolationOut[]) => {
			e.preventDefault();
			e.stopPropagation();
			if (violations.length === 0) {
				return;
			}

			await reopenPolicyResult(
				{
					pathParams: {
						resultId: violations[0].id,
					},
				},
				{
					onSuccess: async () => {
						await queryClient.invalidateQueries({
							queryKey: apiQueryKey('policy/{policy_id}/results', {
								policy_id: policy.id,
							}),
						});
						showNotification({
							title: 'Success',
							message: 'The issue has been re-opened',
							color: 'green',
							icon: <Icon name="check" color="text/success-on-fill/default" />,
						});

						// If the lsat ignored result is re-opened, navigate to the issues tab
						if (ignoredResults.length === 1) {
							onTabChange('issues');
						}
					},
				}
			);
		},
		[reopenPolicyResult, ignoredResults, policy.id, onTabChange]
	);

	return (
		<Stack pt="lg">
			<PolicySection
				title="Ignored resources"
				spacing={false}
				content={ignoredResults.map((result) => (
					<IgnoredResourceRow
						key={result.entity.id}
						result={result}
						policy={policy}
						handleReopenIssue={handleReopenIssue}
						readOnly={readOnly}
					/>
				))}
			/>
		</Stack>
	);
}
